/**
 * Agents Domain Types
 */
import { z } from "zod";

export * from "./agent.types";
export * from "./agent-deployment-settings.types";
export * from "./agent-prompt.constants";
export * from "./agent-prompt.types";
export * from "./agent-prompt.utils";
export * from "./agent-prompt-conversion.utils";
export * from "./agent-prompt-cue.utils";
export * from "./agent-prompt-knowledge.utils";
export * from "./agent-prompt-markdown.constants";
export * from "./ensure-deployment-types-in-prompt-json";
export * from "./markdown-list-markers";
export * from "./markdown-serializer";

// `markdown-parser` and `heal-prompt-escape-cascade` are deliberately NOT
// re-exported as VALUES here.
//
// They pull `marked`, which declares no `sideEffects` field and mutates its own
// export object at module scope, so a bundler may not drop it once it is in the
// graph — even though every function in `markdown-parser` itself tree-shakes
// away cleanly. Re-exported from this barrel, `marked` rode into the frontend's
// EAGER payload: 92 kB of parser downloaded by every route, while no frontend
// production module calls the parser at all.
//
// The TYPES stay on the root, because a type-only re-export erases at compile
// time and pulls no runtime module. Import the FUNCTIONS by subpath:
//   @nexus/types/agents/markdown-parser
//   @nexus/types/agents/heal-prompt-escape-cascade
export type { ParserContext } from "./markdown-parser";
export * from "./prompt-version.types";
export * from "./resolve-deployment-context-keys";

// Re-export ActionSchema and ActionType for the tools domain
export const ActionSchema = z.object({
  path: z.string(),
  method: z.string(),
  parametersFields: z.array(z.any()).optional().default([]),
  bodyFields: z.array(z.any()).optional().default([]),
  operationId: z.string(),
  description: z.string().optional(),
  disabled: z.boolean().optional(),
  skipUserSetup: z.boolean().optional()
});

export type ActionType = z.infer<typeof ActionSchema>;

// Task-specific types
export type AgentClassificationTaskCategory = {
  id: number;
  name: string;
  description: string;
};

export type AgentExtractionTaskData = {
  id: number;
  name: string;
  description: string;
};

export type AgentSummarisationTaskElement = {
  id: number;
  element: string;
  description: string;
};

// Chat command types
export const DEFAULT_CHAT_COMMAND = "default";

export const chatCommandsDescription = {
  default: "Default chat behavior",
  focused: "Focused mode with minimal context",
  creative: "Creative mode with higher temperature"
} as const;

// Copy Agent Schema
export const copyAgentInputSchema = z.object({
  agentId: z.string(),
  organizationId: z.string()
});

export type CopyAgentInputSchemaType = z.infer<typeof copyAgentInputSchema>;
